import { AttributeType } from "@evergis/api";
import { ComparisonOperator, FilterTypeOption, IconTypesKeys } from "@evergis/ui";

import {
  dateComparisonOperators,
  numberComparisonOperators,
  paramComparisonOperators,
  textComparisonOperators,
} from "../constants";

import { i18n } from "../../../i18n";

type ComparisonOptionsValue = Omit<FilterTypeOption, "value" | "text"> & {
  icon?: IconTypesKeys;
  localizationKey?: string;
};

export const COMPARISON_OPTIONS: Record<
  Exclude<ComparisonOperator, ComparisonOperator.Between | ComparisonOperator.Outside>,
  ComparisonOptionsValue
> = {
  [ComparisonOperator.Equals]: {
    icon: "equals",
    autocomplete: true,
    localizationKey: "equals",
  },
  [ComparisonOperator.NotEquals]: {
    icon: "not_equals",
    autocomplete: true,
    localizationKey: "notEquals",
  },
  [ComparisonOperator.GreaterThan]: {
    icon: "greater_than",
    localizationKey: "greaterThan",
  },
  [ComparisonOperator.GreaterOrEquals]: {
    icon: "greater_or_equal",
    localizationKey: "greaterOrEqual",
  },
  [ComparisonOperator.LessThan]: {
    icon: "less_than",
    localizationKey: "lessThan",
  },
  [ComparisonOperator.LessOrEquals]: {
    icon: "less_or_equal",
    localizationKey: "lessOrEqual",
  },
  [ComparisonOperator.Contains]: {
    icon: "contains",
    localizationKey: "contains",
  },
  [ComparisonOperator.NotContains]: {
    icon: "not_contains",
    localizationKey: "doesNotContain",
  },
  [ComparisonOperator.StartsWith]: {
    icon: "starts_with",
    localizationKey: "startsWith",
  },
  [ComparisonOperator.NotStartsWith]: {
    icon: "not_starts_with",
    localizationKey: "doesNotStartWith",
  },
  [ComparisonOperator.EndsWith]: {
    icon: "ends_with",
    localizationKey: "endsWith",
  },
  [ComparisonOperator.NotEndsWith]: {
    icon: "not_ends_with",
    localizationKey: "doesNotEndWith",
  },
  [ComparisonOperator.Filled]: {
    icon: "filled",
    inactive: true,
    localizationKey: "filled",
  },
  [ComparisonOperator.Empty]: {
    icon: "empty",
    inactive: true,
    localizationKey: "empty",
  },
};

const COMPARISON_OPTION_BETWEEN_DATE: Record<ComparisonOperator.Between, ComparisonOptionsValue> = {
  [ComparisonOperator.Between]: {
    icon: "date",
    localizationKey: "dateOrPeriod",
    range: true,
  },
};

const COMPARISON_OPTION_BETWEEN_NUMBER: Record<ComparisonOperator.Between, ComparisonOptionsValue> = {
  [ComparisonOperator.Between]: {
    icon: "inside_range",
    localizationKey: "insideRange",
    range: true,
  },
};

const COMPARISON_OPTION_OUTSIDE_DATE: Record<ComparisonOperator.Outside, ComparisonOptionsValue> = {
  [ComparisonOperator.Outside]: {
    icon: "date_outside",
    localizationKey: "excludingDateOrPeriod",
    range: true,
  },
};

const COMPARISON_OPTION_OUTSIDE_NUMBER: Record<ComparisonOperator.Outside, ComparisonOptionsValue> = {
  [ComparisonOperator.Outside]: {
    icon: "outside_range",
    localizationKey: "outsideRange",
    range: true,
  },
};

const createComparisonOption = (
  value: ComparisonOperator,
  options?: Record<ComparisonOperator, ComparisonOptionsValue>,
): FilterTypeOption | null => {
  const option = (options || COMPARISON_OPTIONS)[value];

  return option
    ? {
        value,
        ...option,
        text: i18n.t(option.localizationKey, { ns: "layerFilter" }),
      }
    : null;
};

export const getComparisonOperatorByType = (type?: AttributeType): Array<ComparisonOperator> => {
  return type === AttributeType.String
    ? textComparisonOperators
    : type === AttributeType.DateTime
    ? dateComparisonOperators
    : numberComparisonOperators;
};

const NotNullable = <Value>(value: Value) => !!value;

export const getComparisonOperatorOptions = (type?: AttributeType, fromTable?: boolean): FilterTypeOption[] => {
  const options = getComparisonOperatorByType(type).map(operator => createComparisonOption(operator));

  if (type === AttributeType.DateTime) {
    options.push(
      createComparisonOption(
        ComparisonOperator.Between,
        COMPARISON_OPTION_BETWEEN_DATE as Record<ComparisonOperator, ComparisonOptionsValue>,
      ),
      createComparisonOption(
        ComparisonOperator.Outside,
        COMPARISON_OPTION_OUTSIDE_DATE as Record<ComparisonOperator, ComparisonOptionsValue>,
      ),
    );
  } else if ([AttributeType.Int32, AttributeType.Int64, AttributeType.Double].includes(type)) {
    options.push(
      createComparisonOption(
        ComparisonOperator.Between,
        COMPARISON_OPTION_BETWEEN_NUMBER as Record<ComparisonOperator, ComparisonOptionsValue>,
      ),
      createComparisonOption(
        ComparisonOperator.Outside,
        COMPARISON_OPTION_OUTSIDE_NUMBER as Record<ComparisonOperator, ComparisonOptionsValue>,
      ),
    );
  }

  if (fromTable && type === AttributeType.String) {
    options.push({
      value: "SeveralValues",
      icon: "checkboxes",
      text: i18n.t("severalValues", { ns: "layerFilter" }),
      checkboxes: true,
    });
  }

  return options.filter(NotNullable);
};

export const getParamOperatorOptions = () => paramComparisonOperators.map(operator => createComparisonOption(operator));
